import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";
import { BsArrowUpCircle } from "react-icons/bs";
import { useState, useRef, Fragment, useEffect, useCallback, useLayoutEffect } from "react";
import { LuCheck, LuCheckCheck } from "react-icons/lu";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MediaPopup from "./modal/MediaPopup";
import useSelectVideo from "../../hooks/useSelectVideo";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { accountBlockUser } from "../../redux/ApiSlice/AccountProfileSlice";
import { accountUnBlockUser } from "../../redux/ApiSlice/BlockSlice";
import { reactToMessage, reportMessage, reportChatMessage, muteUnmuteMessageChat, deleteChatRoom, deleteMessage, editMessage } from "../../redux/ApiSlice/MessagesSlice";
import { getReportReasons } from "../../redux/ApiSlice/ReportSlice";
import { Loader2, X } from "lucide-react";
import DeleteChatModel from "./modal/DeleteChatModel";
import DeleteMessageModel from "./modal/DeleteMessageModel";
import BlockChatModel from "./modal/BlockChatModel";
import UserReportChatModel from "./modal/UserReportChatModel";
import ReportMessageModel from "./modal/ReportMessageModel";
import ChatRoomHeader from "../../components/message/chatroom/ChatRoomHeader";
import ChatMessage from "../../components/message/chatroom/ChatMessage";

const MessageChatRoom = () => {
    const { id } = useParams();
    const menuRef = useRef(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const auth = useSelector((state) => state.auth);
    const { reportReasons: reportReasonOptions = [], reportReasonsLoading: reportReasonOptionsLoading } = useSelector((state) => state.Report);
    const location = useLocation();
    const userInfo = location.state?.from;
    const selectVideo = useSelectVideo();
    const [showMenu, setShowMenu] = useState(false);
    const [showBlockUser, setShowBlockUser] = useState(false);
    const [showDeleteChat, setShowDeleteChat] = useState(false);
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);

    // WebSocket state
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [chatRoomData, setChatRoomData] = useState(null);
    const [chatRoomBlockStatus, setChatRoomBlockStatus] = useState({});
    const [loading, setLoading] = useState(true);
    const [roomDetailReceived, setRoomDetailReceived] = useState(false);
    const [previousMessagesReceived, setPreviousMessagesReceived] = useState(false);

    // Message seen state
    const [seenMessages, setSeenMessages] = useState(new Set());

    // Reconnection state
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const [isReconnecting, setIsReconnecting] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false); // Add this to prevent multiple connections
    const reconnectTimeoutRef = useRef(null);
    const currentWsRef = useRef(null); // Track current WebSocket instance
    const isConnectingRef = useRef(false); // Track connecting state with ref to avoid closure issues
    const maxReconnectAttempts = 5;

    // Pagination state
    const [loadingMore, setLoadingMore] = useState(false);
    const isLoadingMoreRef = useRef(false);
    const messagesContainerRef = useRef(null);
    const prevScrollHeightRef = useRef(0);
    const skipScrollRef = useRef(false);

    // Popup state for media
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupType, setPopupType] = useState(null);
    const [popupSrc, setPopupSrc] = useState("");

    // For video hover play
    const [hoveredVideo, setHoveredVideo] = useState(null);

    // Ref for auto-scroll to bottom
    const messagesEndRef = useRef(null);

    const [reportChatRoomId, setReportChatRoomId] = useState(null);

    // Message report state
    const [reportMessageId, setReportMessageId] = useState(null);
    const [showReportMessage, setShowReportMessage] = useState(false);
    const [hoveredMessageId, setHoveredMessageId] = useState(null);
    const [messageReportStep, setMessageReportStep] = useState("reason");
    const [messageReportReason, setMessageReportReason] = useState(null);
    const [messageReportSubReason, setMessageReportSubReason] = useState("");
    const [reportDetails, setReportDetails] = useState("");
    const [reportLoading, setReportLoading] = useState(false);
    const [reportMessageError, setReportMessageError] = useState("");
    const [messageReportSuccess, setMessageReportSuccess] = useState(false);
    const [messageReportSuccessText, setMessageReportSuccessText] = useState("");

    // User report state
    const [showReportUserModal, setShowReportUserModal] = useState(false);
    const [userReportStep, setUserReportStep] = useState("reason");
    const [userReportReason, setUserReportReason] = useState(null);
    const [userReportSubReason, setUserReportSubReason] = useState("");
    const [userReportDetails, setUserReportDetails] = useState("");
    const [userReportBlockUser, setUserReportBlockUser] = useState(false);
    const [userReportLoading, setUserReportLoading] = useState(false);
    const [userReportError, setUserReportError] = useState("");
    const [userReportSuccess, setUserReportSuccess] = useState(false);

    // Block user confirmation state
    const [showBlockConfirm, setShowBlockConfirm] = useState(false);

    // Delete chat confirmation state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Delete message confirmation state
    const [showDeleteMessageConfirm, setShowDeleteMessageConfirm] = useState(false);
    const [pendingDeleteMessageId, setPendingDeleteMessageId] = useState(null);

    // Edit message state
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const inputRef = useRef(null);

    // Hover timeout management
    const hoverTimeoutRef = useRef(null);

    // Error toast tracking
    const [isShowingErrorToast, setIsShowingErrorToast] = useState(false);
    const errorToastTimeoutRef = useRef(null);

    const menuItems = [
        // {
        //     label: chatRoomData?.is_muted ? "Unmute" : "Mute",
        //     action: () => {
        //         if (chatRoomData?.is_muted) {
        //             toast.success("Unmute Successfully");
        //         } else {
        //             toast.success("Mute Successfully");
        //         }
        //         setShowMenu(false);
        //         dispatch(muteUnmuteMessageChat(chatRoomData?.id));
        //     }
        // },
        {
            label: "Report",
            action: () => {
                setReportChatRoomId(chatRoomData?.id);
                dispatch(getReportReasons());
                setUserReportStep("reason");
                setUserReportReason(null);
                setUserReportSubReason("");
                setUserReportDetails("");
                setUserReportBlockUser(false);
                setUserReportError("");
                setUserReportSuccess(false);
                setShowReportUserModal(true);
                setShowMenu(false);
            }
        },
        {
            label: "Block User",
            action: () => {
                setShowBlockConfirm(true);
                setShowMenu(false);
            }
        },
        {
            label: "Delete Chat",
            action: () => {
                setShowDeleteConfirm(true);
                setShowMenu(false);
            }
        },
    ];

    // WebSocket connection effect
    useEffect(() => {
        if (!id) return;

        // Reset states for new room
        setLoading(true);
        setChatRoomData(null);
        setRoomDetailReceived(false);
        setPreviousMessagesReceived(false);
        setReconnectAttempts(0);
        setIsReconnecting(false);
        setIsConnecting(false); // Reset connecting state
        isConnectingRef.current = false;
        setSeenMessages(new Set());
        setLoadingMore(false);
        isLoadingMoreRef.current = false;
        prevScrollHeightRef.current = 0;

        const getAuthToken = () => auth.accessToken || '';

        let currentWs = null; // Track current WebSocket instance

        const connectWebSocket = () => {
            try {
                // Prevent multiple simultaneous connection attempts
                if (isConnectingRef.current) {
                    return null;
                }

                // Close existing connection if any
                if (currentWs && currentWs.readyState !== WebSocket.CLOSED) {
                    currentWs.close(1000, 'New connection attempt');
                }

                const token = getAuthToken();
                if (!token) {
                    console.error('No auth token found');
                    setLoading(false);
                    toast.error('Authentication required');
                    return null;
                }

                const wsUrl = `${process.env.REACT_APP_SOCKET_URL}/chat/${id}/?token=${token}`;

                setIsConnecting(true);
                isConnectingRef.current = true;

                const ws = new WebSocket(wsUrl);
                currentWs = ws; // Store reference

                ws.onopen = () => {
                    setIsConnected(true);
                    setSocket(ws);
                    setReconnectAttempts(0); // Reset reconnect attempts on successful connection
                    setIsReconnecting(false);
                    setIsConnecting(false);
                    isConnectingRef.current = false;
                };

                ws.onclose = (event) => {

                    // Only process if this is the current connection
                    if (ws !== currentWs) {
                        return;
                    }

                    setIsConnected(false);
                    setSocket(null);
                    setIsConnecting(false);
                    isConnectingRef.current = false;
                    currentWs = null; // Clear reference

                    // Only set loading to false if it's not a manual close (code 1000)
                    if (event.code === 1000) {
                        setLoading(false);
                        setIsReconnecting(false);
                        return; // Don't attempt to reconnect if manually closed
                    }

                    // Check if we should attempt reconnection
                    setReconnectAttempts(prevAttempts => {
                        if (prevAttempts < maxReconnectAttempts) {
                            const delay = Math.min(1000 * Math.pow(2, prevAttempts), 10000); // Exponential backoff, max 10s

                            setIsReconnecting(true);
                            reconnectTimeoutRef.current = setTimeout(() => {
                                setIsReconnecting(false);

                                // Check if component is still mounted and we don't have an active connection
                                if (!currentWs && !isConnectingRef.current) {
                                    // Simply create a new connection
                                    const newWs = connectWebSocket();
                                    if (newWs) {
                                        setSocket(newWs);
                                    }
                                } else {
                                }
                            }, delay);

                            return prevAttempts + 1;
                        } else {
                            console.error('Max reconnection attempts reached');
                            setLoading(false);
                            setIsReconnecting(false);
                            toast.error('Connection failed. Please refresh the page.');
                            return prevAttempts;
                        }
                    });
                };

                ws.onerror = (error) => {
                    console.error('Chat WebSocket error:', error);
                    setIsConnected(false);
                    setIsConnecting(false);
                    isConnectingRef.current = false;
                    // Don't set loading to false here, let onclose handle reconnection
                };

                ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);

                        switch (data.type) {
                            case "block_status":
                                setChatRoomBlockStatus(data);
                                break;
                            case 'room_detail':
                                setChatRoomData(prevData => ({
                                    ...data.data,
                                    messages: prevData?.messages || [],
                                    hasMorePrevious: prevData?.hasMorePrevious ?? false
                                }));
                                setRoomDetailReceived(true);
                                // Extract seen message IDs from room detail messages if available
                                if (data.data.messages) {
                                    const seenIds = extractSeenMessageIds(data.data.messages);
                                    setSeenMessages(seenIds);
                                }
                                break;
                            case 'message':
                                setChatRoomData(prev => ({
                                    ...prev,
                                    messages: [...(prev?.messages || []), data.data]
                                }));
                                break;
                            case 'previous_messages':
                                if (isLoadingMoreRef.current) {
                                    // Load more — prepend older messages, keep scroll position
                                    setChatRoomData(prev => ({
                                        ...prev,
                                        messages: [...(data.data || []), ...(prev?.messages || [])],
                                        hasMorePrevious: data.has_more || false
                                    }));
                                    const loadMoreSeenIds = extractSeenMessageIds(data.data);
                                    setSeenMessages(prev => new Set([...prev, ...loadMoreSeenIds]));
                                    isLoadingMoreRef.current = false;
                                    setLoadingMore(false);
                                } else {
                                    // Initial load — replace messages entirely
                                    setChatRoomData(prev => ({
                                        ...prev,
                                        messages: data.data || [],
                                        hasMorePrevious: data.has_more || false
                                    }));
                                    setPreviousMessagesReceived(true);
                                    setLoading(false);
                                    const seenIds = extractSeenMessageIds(data.data);
                                    setSeenMessages(seenIds);
                                }
                                break;
                            case 'seen_update':
                                setSeenMessages(prev => {
                                    const newSeenMessages = new Set([...prev, data.message_id]);
                                    return newSeenMessages;
                                });
                                break;
                            case 'message_deleted':
                                setChatRoomData(prev => ({
                                    ...prev,
                                    messages: (prev?.messages || []).filter(msg => msg.id !== data.message_id)
                                }));
                                break;
                            case 'message_edited':
                                skipScrollRef.current = true;
                                setChatRoomData(prev => ({
                                    ...prev,
                                    messages: (prev?.messages || []).map(msg =>
                                        msg.id === data.message_id
                                            ? { ...msg, message: data.new_text, is_edited: true, edited_at: data.edited_at }
                                            : msg
                                    )
                                }));
                                break;
                            case 'reaction':
                                skipScrollRef.current = true;
                                setChatRoomData(prev => ({
                                    ...prev,
                                    messages: (prev?.messages || []).map(msg =>
                                        msg.id === data.message_id
                                            ? { ...msg, is_reacted: data.is_reacted, reaction_count: data.reaction_count }
                                            : msg
                                    )
                                }));
                                break;
                            default:
                        }
                    } catch (error) {
                        console.error('Error parsing WebSocket message:', error);
                    }
                };

                return ws;
            } catch (error) {
                console.error('Failed to connect WebSocket:', error);
                setLoading(false);
                setIsConnecting(false);
                isConnectingRef.current = false;
                return null;
            }
        };

        const ws = connectWebSocket();

        // Fallback timeout to prevent infinite loading
        const timeoutId = setTimeout(() => {
            if (loading) {
                console.warn('WebSocket connection timeout, stopping loading');
                setLoading(false);
            }
        }, 10000); // 10 seconds timeout

        // Cleanup on unmount or id change
        return () => {
            clearTimeout(timeoutId);
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
            if (errorToastTimeoutRef.current) {
                clearTimeout(errorToastTimeoutRef.current);
            }
            if (currentWs) {
                currentWs.close(1000, 'Component unmounting');
                currentWsRef.current = null;
            }
            setSocket(null);
            setIsConnected(false);
            setChatRoomData(null);
            setRoomDetailReceived(false);
            setPreviousMessagesReceived(false);
            setReconnectAttempts(0);
            setIsReconnecting(false);
            setIsConnecting(false); // Reset connecting state
            isConnectingRef.current = false;
            setLoadingMore(false);
            isLoadingMoreRef.current = false;
            prevScrollHeightRef.current = 0;
            setLoading(true);
        };
    }, [id]); // Remove reconnectAttempts from dependencies to prevent infinite loop

    // Mark messages as read when chatRoomData is available
    // useEffect(() => {
    //     if (chatRoomData?.id) {
    //         const timer = setTimeout(() => {
    //             dispatch(markMessagesAsRead(chatRoomData.id));
    //         }, 1000);

    //         return () => clearTimeout(timer);
    //     }
    // }, [dispatch, chatRoomData?.id]);

    // Scroll to bottom for new messages, or restore position after prepending older ones
    useLayoutEffect(() => {
        if (skipScrollRef.current) {
            skipScrollRef.current = false;
            return;
        }
        if (prevScrollHeightRef.current > 0 && messagesContainerRef.current) {
            // Restore scroll position so the view doesn't jump after prepend
            const newScrollHeight = messagesContainerRef.current.scrollHeight;
            messagesContainerRef.current.scrollTop = newScrollHeight - prevScrollHeightRef.current;
            prevScrollHeightRef.current = 0;
            return;
        }
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "auto" });
        }
    }, [chatRoomData?.messages]);

    // Close the menu when clicking outside
    useOutsideClick(menuRef, () => setShowMenu(false));

    // Handle dropdown and modal interactions
    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                if (showReportMessage) {
                    setShowReportMessage(false);
                    setMessageReportStep("reason");
                    setMessageReportReason(null);
                    setMessageReportSubReason("");
                    setReportDetails("");
                    setReportMessageError("");
                    setMessageReportSuccess(false);
                    setReportMessageId(null);
                }
                if (showReportUserModal) {
                    setShowReportUserModal(false);
                    setUserReportStep("reason");
                    setUserReportReason(null);
                    setUserReportSubReason("");
                    setUserReportDetails("");
                    setUserReportBlockUser(false);
                    setUserReportError("");
                    setUserReportSuccess(false);
                    setReportChatRoomId(null);
                }
                if (showBlockConfirm) {
                    setShowBlockConfirm(false);
                }
                if (showDeleteConfirm) {
                    setShowDeleteConfirm(false);
                }
            }
        };

        if (showReportMessage || showReportUserModal || showBlockConfirm || showDeleteConfirm) {
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [showReportMessage, showReportUserModal, showBlockConfirm, showDeleteConfirm]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (editingMessageId) {
            await handleEditSubmit(editingMessageId);
            return;
        }

        if (sending || !message.trim() || !socket || !isConnected) {
            if (!message.trim() && !isShowingErrorToast) {
                toast.error("Please enter a message.");
                setIsShowingErrorToast(true);

                if (errorToastTimeoutRef.current) {
                    clearTimeout(errorToastTimeoutRef.current);
                }

                errorToastTimeoutRef.current = setTimeout(() => {
                    setIsShowingErrorToast(false);
                }, 3000);
            }
            return;
        }

        setSending(true);
        try {
            const messageData = {
                type: "message",
                message: message.trim()
            };

            socket.send(JSON.stringify(messageData));
            setMessage("");
        } catch (err) {
            console.error('Error sending message:', err);
            toast.error("Failed to send message");
        }
        setSending(false);
    };

    const handleReaction = (messageId) => {
        if (!socket || !isConnected) return;
        skipScrollRef.current = true;
        socket.send(JSON.stringify({ type: "react", message_id: messageId }));
    };

    const handleDeleteMessage = (messageId) => {
        setPendingDeleteMessageId(messageId);
        setShowDeleteMessageConfirm(true);
    };

    const handleDeleteMessageConfirm = async () => {
        setShowDeleteMessageConfirm(false);
        try {
            const result = await dispatch(deleteMessage(pendingDeleteMessageId));
            if (result.error) {
                toast.error("Failed to delete message.");
            }
        } catch {
            toast.error("Failed to delete message.");
        } finally {
            setPendingDeleteMessageId(null);
        }
    };

    const handleDeleteMessageCancel = () => {
        setShowDeleteMessageConfirm(false);
        setPendingDeleteMessageId(null);
    };

    const handleEditMessage = (messageId, currentText) => {
        setEditingMessageId(messageId);
        setMessage(currentText);
        setTimeout(() => inputRef.current?.focus(), 50);
    };

    const handleEditCancel = () => {
        setEditingMessageId(null);
        setMessage("");
    };

    const handleEditSubmit = async (messageId) => {
        const trimmed = message.trim();
        if (!trimmed) {
            toast.error("Message cannot be empty.");
            return;
        }
        setEditLoading(true);
        try {
            const result = await dispatch(editMessage({ messageId, message: trimmed }));
            if (result.error) {
                toast.error(result.payload?.detail || "Failed to edit message.");
            } else {
                skipScrollRef.current = true;
                setChatRoomData(prev => ({
                    ...prev,
                    messages: (prev?.messages || []).map(msg =>
                        msg.id === messageId
                            ? { ...msg, message: trimmed, is_edited: true }
                            : msg
                    )
                }));
                setEditingMessageId(null);
                setMessage("");
            }
        } catch {
            toast.error("Failed to edit message.");
        } finally {
            setEditLoading(false);
        }
    };

    // Report message functionality
    const handleMessageMouseEnter = (messageId) => {
        // Clear any existing timeout
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        setHoveredMessageId(messageId);
    };

    const handleMessageMouseLeave = () => {
        // Set timeout to hide the button after 2 seconds
        hoverTimeoutRef.current = setTimeout(() => {
            setHoveredMessageId(null);
        }, 2000);
    };

    const handleUserReasonSelect = (selectedReason) => {
        setUserReportReason(selectedReason);
        setUserReportSubReason("");
        setUserReportDetails("");
        setUserReportStep("detail");
        setUserReportError("");
    };

    const handleBackToUserReportReasons = () => {
        setUserReportStep("reason");
        setUserReportSubReason("");
        setUserReportError("");
    };

    const handleSubmitUserReport = async () => {
        if (!userReportReason) {
            toast.error("Please select a reason for reporting the user.");
            return;
        }

        if (userReportReason?.sub_reasons?.length > 0 && !userReportSubReason) {
            toast.error("Choose a sub-reason to continue");
            return;
        }

        const data = {
            chat_room: reportChatRoomId,
            reason: userReportReason.code,
            sub_reason: userReportSubReason || "",
            description: userReportDetails,
            blocked: userReportBlockUser,
        };

        try {
            setUserReportLoading(true);
            setUserReportError("");
            const result = await dispatch(reportMessage(data));

            if (result.payload?.non_field_errors) {
                toast.error(result.payload.non_field_errors[0]);
                setUserReportError(result.payload.non_field_errors[0]);
            } else {
                toast.success("User reported successfully.");
                if (userReportBlockUser) {
                    setChatRoomBlockStatus(prev => ({ ...prev, blocked_by_me: true }));
                }
                setUserReportSuccess(true);
                setReportChatRoomId(null);
            }
        } catch (error) {
            toast.error("Error reporting user");
            console.error("Error reporting user:", error);
        } finally {
            setUserReportLoading(false);
        }
    };

    const handleBlockConfirm = async () => {
        try {
            const userId = chatRoomData?.users?.[0]?.id;
            if (userId) {
                await dispatch(accountBlockUser(userId));
                setShowBlockConfirm(false);
                navigate("/message");
                toast.success("User blocked successfully");
            }
        } catch (error) {
            toast.error("Error blocking user");
        }
    };

    const handleBlockCancel = () => {
        setShowBlockConfirm(false);
    };

    const handleUnblock = async () => {
        try {
            const userId = chatRoomData?.users?.[0]?.id;
            if (userId) {
                await dispatch(accountUnBlockUser(userId));
                setChatRoomBlockStatus(prev => ({ ...prev, blocked_by_me: false }));
                toast.success("User unblocked successfully");
            }
        } catch (error) {
            toast.error("Error unblocking user");
        }
    };

    const handleDeleteConfirm = () => {
        dispatch(deleteChatRoom(id));
        setShowDeleteConfirm(false);
        toast.success("Chat deleted successfully");
        navigate("/message");
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
    };

    const handleReportMessage = (messageId) => {
        setReportMessageId(messageId);
        dispatch(getReportReasons());
        setMessageReportStep("reason");
        setMessageReportReason(null);
        setMessageReportSubReason("");
        setReportDetails("");
        setReportMessageError("");
        setMessageReportSuccess(false);
        setShowReportMessage(true);
    };

    const handleMessageReasonSelect = (selectedReason) => {
        setMessageReportReason(selectedReason);
        setMessageReportSubReason("");
        setReportDetails("");
        setMessageReportStep("detail");
        setReportMessageError("");
    };

    const handleBackToMessageReportReasons = () => {
        setMessageReportStep("reason");
        setMessageReportSubReason("");
        setReportMessageError("");
    };

    const handleSubmitReportMessage = async () => {
        if (!messageReportReason) {
            toast.error("Please select a reason for reporting the message.");
            return;
        }

        if (messageReportReason?.sub_reasons?.length > 0 && !messageReportSubReason) {
            toast.error("Choose a sub-reason to continue");
            return;
        }

        const data = {
            chat_room: chatRoomData?.id,
            reason: messageReportReason.code,
            sub_reason: messageReportSubReason || "",
            description: reportDetails,
            reported_user: chatRoomData?.users?.[0]?.id
        };

        try {
            setReportLoading(true);
            setReportMessageError("");
            const result = await dispatch(reportChatMessage({ messageId: reportMessageId, data }));

            if (reportChatMessage.fulfilled.match(result)) {
                setMessageReportSuccess(true);
                toast.success("Message reported successfully");
            } else if (reportChatMessage.rejected.match(result)) {
                const errorMessage = result.payload?.message || "Failed to report message";
                toast.error(errorMessage);
                setReportMessageError(errorMessage);
            }
        } catch (error) {
            toast.error("Error reporting message");
            console.error("Error reporting message:", error);
        } finally {
            setReportLoading(false);
        }
    };

    // Helper to format date header with time
    function formatDateHeader(dateString) {
        const now = new Date();
        const date = new Date(dateString);

        // Calendar day comparison
        const nowYear = now.getFullYear();
        const nowMonth = now.getMonth();
        const nowDate = now.getDate();
        const dateYear = date.getFullYear();
        const dateMonth = date.getMonth();
        const dateDate = date.getDate();

        // Format time as h:mm AM/PM (uppercase)
        let timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        timeString = timeString.replace(/am|pm/i, match => match.toUpperCase());

        // Helper for month abbreviation in caps
        const monthAbbr = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear();
        const weekdayShort = date.toLocaleString('en-US', { weekday: 'short' }).toUpperCase();

        // Today
        if (nowYear === dateYear && nowMonth === dateMonth && nowDate === dateDate) {
            return `TODAY ${timeString}`;
        }
        // Yesterday
        const yesterday = new Date(now);
        yesterday.setDate(nowDate - 1);
        if (
            dateYear === yesterday.getFullYear() &&
            dateMonth === yesterday.getMonth() &&
            dateDate === yesterday.getDate()
        ) {
            return `YESTERDAY ${timeString}`;
        }
        // Same week
        const diffMs = now - date;
        const diffDay = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        if (diffDay < 7) return `${weekdayShort} ${timeString}`;
        if (nowYear === dateYear) return `${day} ${monthAbbr} ${timeString}`;
        return `${day} ${monthAbbr} ${year} ${timeString}`;
    }

    // Helper to format time only
    function formatTimeOnly(dateString) {
        const date = new Date(dateString);
        let timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        return timeString.replace(/am|pm/i, match => match.toUpperCase());
    }

    // Helper to get date key for grouping messages
    function getDateKey(dateString) {
        const date = new Date(dateString);
        return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    }

    // Group messages by date and determine which ones should show date header
    const processedMessages = (() => {
        if (!chatRoomData?.messages) return [];

        // First, group messages by date
        const messagesByDate = {};
        chatRoomData.messages.forEach(msg => {
            const dateKey = getDateKey(msg.created_at);
            if (!messagesByDate[dateKey]) {
                messagesByDate[dateKey] = [];
            }
            messagesByDate[dateKey].push(msg);
        });

        // Process messages and add date headers with latest message time
        return chatRoomData.messages.map((msg, index) => {
            const currentDateKey = getDateKey(msg.created_at);

            // Show date header if it's the first message of the date group
            const prevMsg = index > 0 ? chatRoomData.messages[index - 1] : null;
            const prevDateKey = prevMsg ? getDateKey(prevMsg.created_at) : null;
            const isFirstOfDate = !prevMsg || currentDateKey !== prevDateKey;

            // If showing date header, use the latest message time from this date group
            let dateHeader = null;
            if (isFirstOfDate) {
                const messagesInThisDate = messagesByDate[currentDateKey];
                const latestMessage = messagesInThisDate[messagesInThisDate.length - 1];
                dateHeader = formatDateHeader(latestMessage.created_at);
            }

            return {
                ...msg,
                showDateHeader: isFirstOfDate,
                dateHeader: dateHeader
            };
        });
    })();

    // Helper function to extract seen message IDs from messages
    const extractSeenMessageIds = (messages) => {
        if (!messages) return new Set();

        const seenIds = new Set();
        messages.forEach(message => {
            // Only add messages that are sent by current user and have read: true
            if (message.read === true && message.is_sent_by_current_user) {
                seenIds.add(message.id);
            }
        });
        return seenIds;
    };

    // Helper function to find the last message sent by current user
    const getLastSentMessageId = () => {
        if (!chatRoomData?.messages) return null;

        // Find the last message sent by current user
        for (let i = chatRoomData.messages.length - 1; i >= 0; i--) {
            const message = chatRoomData.messages[i];
            if (message.is_sent_by_current_user) {
                return message.id;
            }
        }
        return null;
    };

    // Helper function to render read receipt
    const renderReadReceipt = (messageId) => {
        const lastSentMessageId = getLastSentMessageId();

        // Only show read receipt for the last message sent by current user
        if (messageId !== lastSentMessageId) {
            return null;
        }

        const isSeen = seenMessages.has(messageId);

        return (
            <div className="flex justify-end mt-1">
                {isSeen ? (
                    <LuCheckCheck className="text-[12px] text-blue-500" />
                ) : (
                    <LuCheck className="text-[12px] text-gray-400" />
                )}
            </div>
        );
    };

    const handleSelectVideo = async (messageId, id) => {
        // Find the message by id
        const selectedMsg = chatRoomData?.messages?.find(msg => msg.id === messageId);
        // If the message has a shared_post, pass it as an array to selectVideo
        if (selectedMsg && selectedMsg.shared_post) {
            selectVideo(id, [selectedMsg.shared_post], location.pathname);
        } else {
            selectVideo(id, [], location.pathname);
        }
    };

    const handleGroupClick = (group) => {
        // Navigate to group page when shared_group is clicked
        navigate(`/group/${group.id}`);
    };

    const handleCancelUserReport = useCallback(() => {
        setShowReportUserModal(false);
        setUserReportStep("reason");
        setUserReportReason(null);
        setUserReportSubReason("");
        setUserReportDetails("");
        setUserReportBlockUser(false);
        setUserReportError("");
        setUserReportSuccess(false);
        setReportChatRoomId(null);
    }, []);

    const handleCancelReportMessage = useCallback(() => {
        setShowReportMessage(false);
        setMessageReportStep("reason");
        setMessageReportReason(null);
        setMessageReportSubReason("");
        setReportDetails("");
        setReportMessageError("");
        setReportMessageId(null);
        setMessageReportSuccess(false);
    }, []);

    const loadMoreMessages = useCallback(() => {
        if (!socket || !isConnected || isLoadingMoreRef.current || !chatRoomData?.hasMorePrevious) { return; }
        const firstMessage = chatRoomData?.messages?.[0];
        if (!firstMessage) {
            return;
        }
        isLoadingMoreRef.current = true;
        setLoadingMore(true);

        if (messagesContainerRef.current) {
            prevScrollHeightRef.current = messagesContainerRef.current.scrollHeight;
        }

        socket.send(JSON.stringify({
            type: "load_more",
            last_message_id: firstMessage.id
        }));
    }, [socket, isConnected, chatRoomData?.hasMorePrevious, chatRoomData?.messages]);

    // Attach scroll listener — re-registers whenever loadMoreMessages updates
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const scrollTop = container.scrollTop;
            if (scrollTop <= 100) {
                loadMoreMessages();
            }
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, [loadMoreMessages]);

    return (
        <Fragment>
            <div className="h-full flex flex-col relative">
                <ChatRoomHeader
                    userInfo={userInfo}
                    chatRoomData={chatRoomData}
                    setShowMenu={setShowMenu}
                    showMenu={showMenu}
                    menuRef={menuRef}
                    menuItems={menuItems}
                />
                <div ref={messagesContainerRef} className="flex-1 px-2.5 md:px-2 pt-1 w-full overflow-y-auto no-scrollbar overscroll-x-none">
                    {loading && (
                        <div className="w-full h-full flex items-center justify-center">
                            <Loader2 className="w-7 h-7 text-[#cad2c5] animate-spin" />
                        </div>
                    )}
                    {/* Load previous messages spinner — shown while fetching older messages */}
                    {!loading && isConnected && loadingMore && (
                        <div className="flex justify-center py-2">
                            <Loader2 className="w-5 h-5 text-gray-400 dark:text-gray-600 animate-spin" />
                        </div>
                    )}
                    {/* Message List */}
                    {processedMessages?.length === 0 && !loading ? (
                        <div className="flex justify-center items-center h-full text-gray-400 dark:text-gray-700 text-sm">
                            No messages yet.
                        </div>
                    ) :
                        <ChatMessage
                            processedMessages={processedMessages}
                            handleSelectVideo={handleSelectVideo}
                            handleGroupClick={handleGroupClick}
                            navigate={navigate}
                            renderReadReceipt={renderReadReceipt}
                            handleReaction={handleReaction}
                            hoveredMessageId={hoveredMessageId}
                            handleMessageMouseEnter={handleMessageMouseEnter}
                            handleMessageMouseLeave={handleMessageMouseLeave}
                            handleReportMessage={handleReportMessage}
                            handleDeleteMessage={handleDeleteMessage}
                            editingMessageId={editingMessageId}
                            handleEditMessage={handleEditMessage}
                        />
                    }
                    {/* Dummy div for scroll-to-bottom */}
                    <div ref={messagesEndRef} className="overscroll-x-none" />
                </div>
                {!chatRoomBlockStatus?.blocked_by_me && !loading && <div className="sticky bottom-0 bg-white dark:bg-black z-10 px-2.5 md:px-2 py-1 flex-shrink-0">
                    {editingMessageId && (
                        <div className="flex items-center justify-between px-2 py-1 mb-1 bg-[#BDE6FF]/20 border-l-2 border-[#BDE6FF] rounded-r-md">
                            <span className="text-[11px] text-[#02488C] dark:text-[#BDE6FF] font-medium">Editing message</span>
                            <button onClick={handleEditCancel} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                <X size={13} />
                            </button>
                        </div>
                    )}
                    <form className="flex items-center justify-between gap-1 my-1" onSubmit={handleSubmit}>
                        <div className="relative w-full">
                            <input
                                ref={inputRef}
                                value={message}
                                placeholder="Send a message..."
                                onChange={e => setMessage(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Escape' && editingMessageId) handleEditCancel(); }}
                                className="text-[14px] w-full py-2 bg-gray-200 dark:bg-gray-900 text-black dark:text-white pl-2 pr-10 focus:outline-none rounded-md border-none"
                            />
                            <button
                                type="submit"
                                disabled={(editLoading) || (!editingMessageId && (sending || !isConnected))}
                                className="absolute top-1.5 right-1.5 bg-[#BDE6FF] flex items-center justify-center h-[26px] w-[26px] rounded-full cursor-pointer z-50 disabled:opacity-50"
                            >
                                {sending || editLoading ? (
                                    <ClipLoader size={10} color="#cad2c5" />
                                ) : (
                                    <BsArrowUpCircle className="text-[18px] text-black" />
                                )}
                            </button>
                        </div>
                    </form>
                </div>}

                {chatRoomBlockStatus?.blocked_by_me && (
                    <div className="sticky bottom-0 bg-white dark:bg-black z-10 px-2.5 md:px-2 py-3 flex-shrink-0 flex flex-col items-center gap-2">
                        <p className="text-gray-400 dark:text-gray-500 text-sm">
                            You have blocked this user.
                        </p>
                        <button
                            onClick={handleUnblock}
                            className="px-6 py-1.5 rounded-full bg-[#BDE6FF] text-black text-sm font-medium hover:opacity-80 transition-opacity"
                        >
                            Unblock
                        </button>
                    </div>
                )}
            </div>
            {/* Report Message Modal */}
            {showReportMessage && (
                <ReportMessageModel
                    messageReportStep={messageReportStep}
                    messageReportReason={messageReportReason}
                    messageReportSubReason={messageReportSubReason}
                    setMessageReportSubReason={setMessageReportSubReason}
                    reportDetails={reportDetails}
                    setReportDetails={setReportDetails}
                    reportMessageError={reportMessageError}
                    messageReportSuccess={messageReportSuccess}
                    handleSubmitReportMessage={handleSubmitReportMessage}
                    reportLoading={reportLoading}
                    handleCancelReportMessage={handleCancelReportMessage}
                    handleBackToMessageReportReasons={handleBackToMessageReportReasons}
                    reportReasons={reportReasonOptions}
                    reportReasonsLoading={reportReasonOptionsLoading}
                    handleMessageReasonSelect={handleMessageReasonSelect}
                />
            )}
            {/* Report User Modal */}
            {showReportUserModal && (
                <UserReportChatModel
                    chatRoomData={chatRoomData}
                    userReportStep={userReportStep}
                    userReportReason={userReportReason}
                    userReportSubReason={userReportSubReason}
                    setUserReportSubReason={setUserReportSubReason}
                    userReportDetails={userReportDetails}
                    setUserReportDetails={setUserReportDetails}
                    userReportBlockUser={userReportBlockUser}
                    setUserReportBlockUser={setUserReportBlockUser}
                    userReportError={userReportError}
                    userReportSuccess={userReportSuccess}
                    handleSubmitUserReport={handleSubmitUserReport}
                    userReportLoading={userReportLoading}
                    handleCancelUserReport={handleCancelUserReport}
                    handleBackToUserReportReasons={handleBackToUserReportReasons}
                    reportReasons={reportReasonOptions}
                    reportReasonsLoading={reportReasonOptionsLoading}
                    handleUserReasonSelect={handleUserReasonSelect}
                />
            )}
            {/* Block User Confirmation Modal */}
            {showBlockConfirm && <BlockChatModel
                chatRoomData={chatRoomData}
                handleBlockCancel={handleBlockCancel}
                handleBlockConfirm={handleBlockConfirm}
            />}
            {/* Delete Chat Confirmation Modal */}
            {showDeleteConfirm && <DeleteChatModel
                chatRoomData={chatRoomData}
                handleDeleteCancel={handleDeleteCancel}
                handleDeleteConfirm={handleDeleteConfirm}
            />}
            {/* Delete Message Confirmation Modal */}
            {showDeleteMessageConfirm && (
                <DeleteMessageModel
                    handleDeleteMessageCancel={handleDeleteMessageCancel}
                    handleDeleteMessageConfirm={handleDeleteMessageConfirm}
                />
            )}
            <MediaPopup
                src={popupSrc}
                type={popupType}
                open={popupOpen}
                onClose={() => setPopupOpen(false)}
            />
        </Fragment>
    );
};

export default MessageChatRoom;